"use client"

import * as React from "react"
import { Calendar, cn, Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@ibada/ui"
import { getCountries, getCountryCallingCode, parsePhoneNumber, type Country as PhoneCountry } from "react-phone-number-input"

import { useLocale, useTranslations } from "@/i18n"

import { MobileFormRow, MobileFormSection } from "@/shared/components/mobile-form/mobile-form"
import { ChevronDownIcon, PlusIcon, XIcon } from "lucide-react"
import {
  MobilePickerSheet,
  type MobilePickerOption,
  type MobilePickerSearch,
  type MobilePickerTab,
} from "@/shared/components/mobile-form/mobile-picker-sheet"
import { MobileAmountInput } from "@/shared/components/mobile-form/mobile-amount-field"
import { MobileTimeWheel } from "@/shared/components/mobile-form/mobile-time-wheel"

type MobileFormSelectRowProps = {
  label: string
  placeholder: string
  /** The selected value ("" / undefined for none). */
  value?: string | null
  onChange: (value: string | undefined, tab?: string) => void
  options?: MobilePickerOption[]
  /** A picker that chooses between kinds; `valueLabel` then names the selection. */
  tabs?: MobilePickerTab[]
  initialTab?: string
  /** What the row shows when the selection is not in `options` (e.g. a tabbed pick, with its kind as a prefix). */
  valueLabel?: React.ReactNode
  /** A second line under the value (e.g. the chosen account's balance). */
  sublabel?: React.ReactNode
  sheetTitle?: string
  doneLabel: string
  searchPlaceholder?: string
  emptyLabel: string
  /** Offer a "none" row in the sheet (an optional field). */
  clearLabel?: string
  /** False for a short fixed list where the sheet's search field is only noise. */
  searchable?: boolean
  invalid?: boolean
  disabled?: boolean
} & MobilePickerSearch

/** A row that opens a sheet picker: the most common field in a phone form. */
export function MobileFormSelectRow({
  label,
  placeholder,
  value,
  onChange,
  options = [],
  tabs,
  initialTab,
  valueLabel,
  sublabel,
  sheetTitle,
  doneLabel,
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  searchable,
  invalid,
  disabled,
  onQueryChange,
  loading,
}: MobileFormSelectRowProps) {
  const [open, setOpen] = React.useState(false)
  const shown = valueLabel ?? options.find((option) => option.value === value)?.label
  return (
    <>
      <MobileFormRow label={label} value={shown} sublabel={sublabel} placeholder={placeholder} invalid={invalid} disabled={disabled} onPress={() => setOpen(true)} />
      <MobilePickerSheet
        open={open}
        onOpenChange={setOpen}
        title={sheetTitle ?? label}
        doneLabel={doneLabel}
        value={value}
        onSelect={(next, tab) => onChange(next, tab)}
        options={options}
        tabs={tabs}
        initialTab={initialTab}
        searchPlaceholder={searchPlaceholder}
        emptyLabel={emptyLabel}
        clearLabel={clearLabel}
        onClear={clearLabel ? () => onChange(undefined) : undefined}
        onQueryChange={onQueryChange}
        loading={loading}
        searchable={searchable}
      />
    </>
  )
}

type MobileFormDateTimeRowProps = {
  label: string
  /** ISO instant. */
  value?: string | null
  onChange: (iso: string) => void
  /** How the row reads the value ("Aujourd'hui, 09:13"). */
  formatValue: (date: Date) => string
  placeholder?: string
  /** Latest allowed instant (ISO), e.g. now for something that already happened. */
  max?: string
  /** Labels for the sheet. */
  doneLabel: string
  timeLabel: string
  invalid?: boolean
  disabled?: boolean
}

const pad2 = (n: number) => String(n).padStart(2, "0")

/** A "yyyy-mm-dd" day as a local date (never shifted by the time zone), or null. */
function parseDay(value: string | null | undefined): Date | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
}

/**
 * A day row (a start or end date, no time). Tapping it opens the shared calendar in the app's sheet; picking a
 * day closes it. Takes and gives "yyyy-mm-dd" ("" for none).
 */
export function MobileFormDateRow({
  label,
  value,
  onChange,
  placeholder,
  clearLabel,
  invalid,
  disabled,
  max,
  fromYear,
  inline = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Inside a sheet: the calendar unfolds under the row instead of opening a second sheet. */
  inline?: boolean
  /** Offers a row that empties the date (an optional field). */
  clearLabel?: string
  invalid?: boolean
  disabled?: boolean
  /** Latest day allowed ("yyyy-mm-dd"); later days cannot be picked (a birth date: today). */
  max?: string
  /** Month and year menus on the calendar from this year (a birth date decades back), instead of arrows only. */
  fromYear?: number
}) {
  const locale = useLocale()
  const maxDay = parseDay(max)
  const [open, setOpen] = React.useState(false)
  const day = parseDay(value)
  const shown = day ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(day) : undefined
  const calendar = (
    <Calendar
      mode='single'
      selected={day ?? undefined}
      defaultMonth={day ?? maxDay ?? undefined}
      disabled={maxDay ? { after: maxDay } : undefined}
      {...(fromYear
        ? { captionLayout: "dropdown" as const, startMonth: new Date(fromYear, 0, 1), endMonth: maxDay ?? new Date(new Date().getFullYear() + 5, 11, 1) }
        : {})}
      onSelect={(next) => {
        if (!next) return
        onChange(`${next.getFullYear()}-${pad2(next.getMonth() + 1)}-${pad2(next.getDate())}`)
        setOpen(false)
      }}
      className='w-full max-w-sm bg-transparent p-0 [--cell-size:2.75rem]'
    />
  )
  if (inline) {
    return (
      <div>
        <MobileFormRow
          label={label}
          value={shown}
          placeholder={placeholder}
          invalid={invalid}
          disabled={disabled}
          onPress={() => setOpen((next) => !next)}
          trailing={<ChevronDownIcon aria-hidden className={cn("size-4 shrink-0 text-muted-foreground/60 transition-transform", open && "rotate-180")} />}
        />
        {open ? <div className='flex justify-center border-t border-border/60 px-3 py-3'>{calendar}</div> : null}
      </div>
    )
  }
  return (
    <>
      <MobileFormRow
        label={label}
        value={shown}
        placeholder={placeholder}
        invalid={invalid}
        disabled={disabled}
        onPress={() => setOpen(true)}
      />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='truncate text-lg font-extrabold'>{label}</DrawerTitle>
            <DrawerDescription className='sr-only'>{label}</DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col items-center gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            {calendar}
            {clearLabel && value ? (
              <button
                type='button'
                onClick={() => {
                  onChange("")
                  setOpen(false)
                }}
                className='flex min-h-[3.25rem] w-full max-w-sm items-center rounded-2xl bg-muted/60 px-4 text-left text-base font-semibold text-muted-foreground active:bg-muted'
              >
                {clearLabel}
              </button>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

/** A country's flag, drawn from its two-letter code. */
function flagOf(iso2: string) {
  return iso2.toUpperCase().replace(/./g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
}

/** Splits a stored number ("+243812345678") into its country and the digits after the dial code. */
function splitPhone(value: string, fallback: PhoneCountry): { country: PhoneCountry; national: string } {
  if (value) {
    try {
      const parsed = parsePhoneNumber(value)
      if (parsed?.country) return { country: parsed.country, national: parsed.nationalNumber }
    } catch {
      // Not a full number yet: keep the fallback country and the digits.
    }
  }
  const code = getCountryCallingCode(fallback)
  const digits = value.replace(/\D/g, "")
  return { country: fallback, national: digits.startsWith(code) ? digits.slice(code.length) : digits }
}

/**
 * A phone number as one row of its group: the flag and dial code as a chip (opening the searchable country
 * sheet), then the number typed beside it with the phone's number keyboard. Gives back the full number
 * ("+243812345678"), as the desktop phone field does. The group's title names it; pass `label` only when the row
 * shares a group with other fields.
 */
export function MobileFormPhoneRow({
  value,
  onChange,
  onBlur,
  disabled,
  defaultCountry = "CD",
  placeholder,
  label,
  invalid = false,
}: {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  disabled?: boolean
  /** The dial code to start on when there is no number yet. */
  defaultCountry?: PhoneCountry
  placeholder?: string
  /** A label on the left, for a phone row among other fields (otherwise the group's title names it). */
  label?: string
  invalid?: boolean
}) {
  const t = useTranslations("common.phoneRow")
  const locale = useLocale()
  const [sheetOpen, setSheetOpen] = React.useState(false)
  // The country is read from the stored number once; after that the person's choice is the authority (several
  // countries share a dial code, so re-guessing it on every keystroke could flip it under them).
  const [country, setCountry] = React.useState<PhoneCountry>(() => splitPhone(value, defaultCountry).country)
  const code = getCountryCallingCode(country)
  const digits = value.replace(/\D/g, "")
  const national = digits.startsWith(code) ? digits.slice(code.length) : digits
  const emit = (nextCountry: PhoneCountry, digits: string) => {
    const clean = digits.replace(/\D/g, "").replace(/^0+/, "")
    onChange(clean ? `+${getCountryCallingCode(nextCountry)}${clean}` : "")
  }
  const options = React.useMemo(() => {
    let names: Intl.DisplayNames | null = null
    try {
      names = new Intl.DisplayNames([locale], { type: "region" })
    } catch {
      names = null
    }
    return getCountries()
      .map((iso) => ({ iso, name: names?.of(iso) ?? iso }))
      .sort((a, b) => a.name.localeCompare(b.name, locale))
      .map(({ iso, name }) => ({ value: iso, label: `${flagOf(iso)}  ${name}`, description: `+${getCountryCallingCode(iso)}` }))
  }, [locale])
  return (
    <div className='flex min-h-[3.25rem] w-full min-w-0 items-center gap-2.5 px-4 text-base'>
      {label ? (
        <span className={cn("max-w-[40%] shrink-0 truncate font-semibold", invalid ? "text-destructive" : "text-foreground")}>{label}</span>
      ) : null}
      <button
        type='button'
        onClick={() => setSheetOpen(true)}
        disabled={disabled}
        aria-label={`${t("country")}: +${code}`}
        className='inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-muted px-3 font-semibold text-foreground active:brightness-95 disabled:opacity-60'
      >
        <span aria-hidden>{flagOf(country)}</span>
        <span className='tabular-nums'>+{code}</span>
        <ChevronDownIcon aria-hidden className='size-4 text-muted-foreground' />
      </button>
      <input
        type='tel'
        inputMode='tel'
        autoComplete='tel-national'
        aria-label={label ?? t("number")}
        value={national}
        onChange={(event) => emit(country, event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder ?? t("number")}
        disabled={disabled}
        className={cn(
          "min-w-0 flex-1 bg-transparent text-base font-semibold tabular-nums outline-none placeholder:text-muted-foreground/70",
          label ? "text-right" : "text-left",
          invalid ? "text-destructive" : "text-foreground",
        )}
      />
      <MobilePickerSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title={t("country")}
        doneLabel={t("done")}
        value={country}
        onSelect={(next) => {
          const nextCountry = next as PhoneCountry
          setCountry(nextCountry)
          emit(nextCountry, national)
        }}
        options={options}
        searchPlaceholder={t("search")}
        emptyLabel={t("noResults")}
      />
    </div>
  )
}

/** An amount typed in a row (a fund's target): the label on the left, the phone's decimal pad and currency on the right. */
export function MobileFormAmountRow({
  label,
  value,
  onChange,
  currencyCode,
  invalid,
  disabled,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  currencyCode: string
  invalid?: boolean
  disabled?: boolean
}) {
  return (
    <label className='flex min-h-[3.25rem] w-full min-w-0 items-center gap-3 px-4 text-left text-base'>
      <span className={invalid ? "max-w-[55%] shrink-0 truncate font-semibold text-destructive" : "max-w-[55%] shrink-0 truncate font-semibold text-foreground"}>
        {label}
      </span>
      <span className='flex min-w-0 flex-1 justify-end'>
        <MobileAmountInput value={value} onChange={onChange} currencyCode={currencyCode} ariaLabel={label} disabled={disabled} />
      </span>
    </label>
  )
}

/** Which half of the date and time sheet is showing. */
enum DateTimePanel {
  Date = "date",
  Time = "time",
}

/** The date / time switch at the top of the sheet: the current value, tinted while its panel is open. */
function DateTimeChip({ active, onClick, ariaLabel, children }: { active: boolean; onClick: () => void; ariaLabel?: string; children: React.ReactNode }) {
  return (
    <button
      type='button'
      aria-pressed={active}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        "h-11 min-w-0 truncate rounded-full px-4 text-base font-extrabold tabular-nums transition-colors",
        active ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  )
}

/**
 * A date and time row. Tapping it opens the app's bottom sheet: a date chip and a time chip on top, and under them
 * the shadcn calendar (the same calendar as the rest of the app) or the app's time wheel. Never the browser's own
 * date or time popup.
 */
export function MobileFormDateTimeRow({
  label,
  value,
  onChange,
  formatValue,
  placeholder,
  max,
  doneLabel,
  timeLabel,
  invalid,
  disabled,
}: MobileFormDateTimeRowProps) {
  const [open, setOpen] = React.useState(false)
  const current = value ? new Date(value) : null
  const valid = current && !Number.isNaN(current.getTime()) ? current : null
  const maxDate = max ? new Date(max) : null
  const [day, setDay] = React.useState<Date>(valid ?? new Date())
  const [time, setTime] = React.useState(valid ? `${pad2(valid.getHours())}:${pad2(valid.getMinutes())}` : "")
  const [panel, setPanel] = React.useState<DateTimePanel>(DateTimePanel.Date)
  const locale = useLocale()

  React.useEffect(() => {
    if (!open) return
    const base = valid ?? new Date()
    setDay(base)
    setTime(`${pad2(base.getHours())}:${pad2(base.getMinutes())}`)
    setPanel(DateTimePanel.Date)
    // Re-seed only when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const confirm = () => {
    const [hours = "0", minutes = "0"] = time.split(":")
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Number(hours), Number(minutes))
    // Something that already happened cannot be dated in the future: clamp to the limit.
    onChange((maxDate && next > maxDate ? maxDate : next).toISOString())
    setOpen(false)
  }

  return (
    <>
      <MobileFormRow
        label={label}
        value={valid ? formatValue(valid) : undefined}
        placeholder={placeholder}
        invalid={invalid}
        disabled={disabled}
        onPress={() => setOpen(true)}
      />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className='flex-row items-center justify-between group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='min-w-0 flex-1 truncate text-lg font-extrabold'>{label}</DrawerTitle>
            <DrawerDescription className='sr-only'>{label}</DrawerDescription>
            <button type='button' onClick={confirm} className='rounded-full px-2 py-1 text-base font-extrabold text-primary active:bg-primary/10'>
              {doneLabel}
            </button>
          </DrawerHeader>
          <div className='flex flex-col items-center gap-4 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            <div className='grid w-full max-w-sm grid-cols-2 gap-2'>
              <DateTimeChip active={panel === DateTimePanel.Date} onClick={() => setPanel(DateTimePanel.Date)}>
                {new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(day)}
              </DateTimeChip>
              <DateTimeChip active={panel === DateTimePanel.Time} onClick={() => setPanel(DateTimePanel.Time)} ariaLabel={timeLabel}>
                {new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(
                  new Date(2000, 0, 1, Number(time.split(":")[0]) || 0, Number(time.split(":")[1]) || 0),
                )}
              </DateTimeChip>
            </div>
            {panel === DateTimePanel.Date ? (
            <Calendar
              mode='single'
              selected={day}
              defaultMonth={day}
              onSelect={(next) => next && setDay(next)}
              disabled={maxDate ? { after: maxDate } : undefined}
              className='w-full max-w-sm bg-transparent p-0 [--cell-size:2.75rem]'
            />
            ) : (
              <div className='flex w-full items-center py-6'>
                <MobileTimeWheel value={time} onChange={setTime} hourLabel={timeLabel} minuteLabel={timeLabel} />
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

/**
 * A time of day row ("HH:mm", 24-hour, like the desktop time picker): the time in the reader's clock on the right,
 * and a sheet with the app's hour and minute wheels. For a day and a time together, use `MobileFormDateTimeRow`.
 */
export function MobileFormTimeRow({
  label,
  value,
  onChange,
  placeholder,
  doneLabel,
  hourLabel,
  minuteLabel,
  invalid,
  disabled,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  doneLabel: string
  hourLabel: string
  minuteLabel: string
  invalid?: boolean
  disabled?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState(value || "09:00")
  const locale = useLocale()

  React.useEffect(() => {
    // Re-seed only when the sheet opens.
    if (open) setDraft(value || "09:00")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const [hours = "", minutes = ""] = value.split(":")
  const display = value
    ? new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(
        new Date(2000, 0, 1, Number(hours) || 0, Number(minutes) || 0),
      )
    : undefined

  return (
    <>
      <MobileFormRow
        label={label}
        value={display}
        placeholder={placeholder}
        invalid={invalid}
        disabled={disabled}
        onPress={() => setOpen(true)}
      />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader className='flex-row items-center justify-between group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='min-w-0 flex-1 truncate text-lg font-extrabold'>{label}</DrawerTitle>
            <DrawerDescription className='sr-only'>{label}</DrawerDescription>
            <button
              type='button'
              onClick={() => {
                onChange(draft)
                setOpen(false)
              }}
              className='rounded-full px-2 py-1 text-base font-extrabold text-primary active:bg-primary/10'
            >
              {doneLabel}
            </button>
          </DrawerHeader>
          <div className='flex w-full items-center px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]'>
            <MobileTimeWheel value={draft} onChange={setDraft} hourLabel={hourLabel} minuteLabel={minuteLabel} />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

/**
 * A multi-choice field as a grouped section: each chosen item as a row with a red remove button, then an "add"
 * row that opens the app's picker with the remaining options. For several people or tags where a single
 * `MobileFormSelectRow` cannot hold the answer.
 */
export function MobileFormMultiSelectSection({
  title,
  footer,
  values,
  onChange,
  options,
  addLabel,
  removeLabel,
  doneLabel,
  emptyLabel,
  searchPlaceholder,
  onQueryChange,
  loading,
  disabled,
}: {
  title?: string
  footer?: React.ReactNode
  values: string[]
  onChange: (values: string[]) => void
  options: MobilePickerOption[]
  addLabel: string
  removeLabel: string
  doneLabel: string
  emptyLabel: string
  searchPlaceholder?: string
  disabled?: boolean
} & MobilePickerSearch) {
  const [open, setOpen] = React.useState(false)
  const labelOf = (value: string) => options.find((option) => option.value === value)?.label ?? value
  const remaining = options.filter((option) => !values.includes(option.value))
  return (
    <>
      <MobileFormSection title={title} footer={footer}>
        {values.map((value) => (
          <div key={value} className='flex min-h-[3.25rem] min-w-0 items-center gap-3 pr-2 pl-4'>
            <span className='min-w-0 flex-1 truncate text-base font-semibold'>{labelOf(value)}</span>
            <button
              type='button'
              aria-label={`${removeLabel}: ${labelOf(value)}`}
              disabled={disabled}
              onClick={() => onChange(values.filter((item) => item !== value))}
              className='inline-flex size-9 shrink-0 items-center justify-center rounded-full text-destructive active:bg-destructive/10 disabled:opacity-45'
            >
              <XIcon aria-hidden className='size-4' />
            </button>
          </div>
        ))}
        <button
          type='button'
          disabled={disabled}
          onClick={() => setOpen(true)}
          className='flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left text-base font-extrabold text-primary active:bg-muted disabled:opacity-45'
        >
          <PlusIcon aria-hidden className='size-5' />
          {addLabel}
        </button>
      </MobileFormSection>
      <MobilePickerSheet
        open={open}
        onOpenChange={setOpen}
        title={addLabel}
        doneLabel={doneLabel}
        emptyLabel={emptyLabel}
        options={remaining}
        searchPlaceholder={searchPlaceholder}
        onQueryChange={onQueryChange}
        loading={loading}
        onSelect={(value) => onChange([...values, value])}
      />
    </>
  )
}
